import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Pagination } from 'swiper';
import { SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { isMediumUI } from 'shared/app/lib/choose-ui-type';
import { ControlledSwiper } from 'console/shared/components/modals/swiper/controlled';
import { useObserveControl } from 'console/shared/contexts/observe-control';
import { HeatingCircuitTemperatureControl } from './components/temperature-control';
import { HeatingCircuitDetails } from './components/details';
import { UiGuidesUtils } from 'console/shared/components/dialogs/heating-circuit-widget/components/ui-guides-utils';
import { useAppSelector } from 'shared/hooks/redux/hooks';
import { useDeviceState } from 'console/shared/contexts/device/state';
export const HeatingCircuitWidget = memo(function HeatingCircuitWidget() {
    const { disabled_modal_interaction } = useObserveControl();
    const { is_service_mode } = useDeviceState();
    const [swiper, setSwiper] = useState(null);
    const [touch, setTouch] = useState({ move: 0, start: 0 });
    const hasScrollbar = useCallback((card_height, scroll_height) => card_height !== scroll_height, []);
    const getSwiperSlideInfo = useCallback(() => {
        if (!swiper) {
            return { clientHeight: 0, scrollTop: 0, scrollHeight: 0 };
        }
        const { slides, activeIndex } = swiper;
        const { clientHeight, scrollTop, scrollHeight } = slides[activeIndex];
        return { clientHeight, scrollTop, scrollHeight };
    }, [swiper?.slides, swiper?.activeIndex]);
    //направление прокрутки
    const direction = useMemo(() => {
        return touch.start > touch.move ? 'down' : 'up';
    }, [touch]);
    //если прокручиваем вниз
    const isScrollingDown = useCallback((scroll_top) => scroll_top === 0 && direction === 'down', [direction]);
    const isScrollingToWidget = useCallback((scroll_top) => scroll_top === 0 && direction === 'up', [direction]);
    //прокручиваем ли
    const isInScrolling = useCallback((scroll_top, card_height, scroll_height) => scroll_top > 0 && scroll_top + card_height < scroll_height, []);
    //если проклучиваем наверх
    const isScrollingUp = useCallback((scroll_top, card_height, scroll_height) => scroll_top + card_height === scroll_height && direction === 'up', [direction]);
    const onDetailsCardScroll = useCallback((event) => {
        const { clientHeight, scrollHeight, scrollTop } = getSwiperSlideInfo();
        const move_touch = event.touches[0].clientY;
        setTouch((prev_state) => ({ ...prev_state, move: move_touch }));
        const has_scrollbar = hasScrollbar(clientHeight, scrollHeight);
        const is_scrolling_down = isScrollingDown(scrollTop);
        const is_scrolling = isInScrolling(scrollTop, clientHeight, scrollHeight);
        const is_scrolling_up = isScrollingUp(scrollTop, clientHeight, scrollHeight);
        if (has_scrollbar &&
            swiper &&
            (is_scrolling_down || is_scrolling || is_scrolling_up)) {
            swiper.allowTouchMove = false;
        }
    }, [swiper, touch]);
    const onDetailsCardScrollStart = useCallback((event) => {
        const { clientHeight, scrollHeight, scrollTop } = getSwiperSlideInfo();
        const start_touch = event.touches[0].clientY;
        const is_scrolling_to_widget = isScrollingToWidget(scrollTop);
        const has_scrollbar = hasScrollbar(clientHeight, scrollHeight);
        if (has_scrollbar && swiper) {
            swiper.allowTouchMove = false;
        }
        if (is_scrolling_to_widget && swiper) {
            swiper.allowTouchMove = true;
        }
        setTouch((prev_state) => ({ ...prev_state, start: start_touch }));
    }, [swiper, touch]);
    if (isMediumUI()) {
        return (_jsxs(Box, { display: "flex", columnGap: 1, width: "100%", alignItems: "center", height: "100%", overflow: "hidden", children: [_jsx(HeatingCircuitTemperatureControl, {}), is_service_mode && _jsx(HeatingCircuitDetails, {})] }));
    }
    const controlVerticalSlide = useCallback(() => {
        if (swiper) {
            if (disabled_modal_interaction) {
                swiper.allowTouchMove = false;
                return;
            }
            swiper.allowTouchMove = true;
        }
    }, [swiper, disabled_modal_interaction]);
    const nextUiGuide = useAppSelector((state) => state.ui_guide_state.store_helpers.next);
    const _nextUiGuide = useCallback(() => {
        nextUiGuide?.();
    }, [nextUiGuide]);
    return (_jsxs(ControlledSwiper, { disabled_drag: disabled_modal_interaction, direction: "vertical", modules: [Pagination], onSwiper: setSwiper, onSlideChange: _nextUiGuide, resistance: true, resistanceRatio: 0, "data-tooltip": "heating-circuit-widget", children: [_jsxs(SwiperSlide, { onTouchStart: controlVerticalSlide, onTouchEnd: controlVerticalSlide, children: [_jsx(UiGuidesUtils, { up: true }), _jsx(HeatingCircuitTemperatureControl, {})] }), _jsxs(SwiperSlide, { onTouchMove: onDetailsCardScroll, onTouchStart: onDetailsCardScrollStart, style: {
                    overflow: 'auto',
                }, children: [_jsx(UiGuidesUtils, {}), _jsx(HeatingCircuitDetails, {})] })] }));
});
